import { useCallback, useEffect, useRef, useState } from "react";
import { useLingui } from "@lingui/macro";

import API from "@app/services/api";

/** The engine's refusal codes (``StudyError`` / ``RegressionError``), worded for the reader. */
export function useRefusalText(): (code: string) => string {
  const { t } = useLingui();
  return useCallback(
    (code: string) => {
      if (code.startsWith("multi_covariate_needs_value"))
        return t`Une variable à plusieurs valeurs doit être lue sur une valeur précise : choisissez laquelle.`;
      if (code.startsWith("unknown_variable"))
        return t`Une variable de l'analyse n'existe pas dans ces cohortes.`;
      switch (code) {
        case "variable_grouping_needs_one_cohort":
          return t`Des groupes définis par une variable demandent une seule cohorte.`;
        case "numeric_grouping_needs_threshold":
          return t`Indiquez le seuil qui sépare les deux groupes.`;
        case "unknown_grouping_variable":
          return t`La variable de regroupement n'existe pas dans cette cohorte.`;
        case "no_group":
          return t`Aucun groupe à analyser : choisissez au moins une valeur.`;
        case "numeric_outcome":
          return t`L'événement doit être une catégorie, pas un nombre.`;
        case "too_many_covariates":
          return t`Douze facteurs au plus.`;
        case "different_specialities":
          return t`Les cohortes appartiennent à des spécialités différentes.`;
        case "unknown_score":
          return t`Ce score n'est pas suivi dans le temps pour cette spécialité.`;
        default:
          return t`L'analyse n'a pas pu être réalisée.`;
      }
    },
    [t],
  );
}

interface ApiAnswer {
  ok?: boolean;
  data?: unknown;
  error?: string;
  detail?: string;
}

/**
 * Run one analysis whenever its request changes — after a short pause, so typing a threshold does
 * not fire a request per keystroke — and keep only the answer to the LATEST request: an earlier,
 * slower one landing last would show a table that no longer matches the settings on screen.
 *
 * A change of language runs it again too: the server words the results in the reader's language.
 */
export function useStudyAnalysis<T>(
  path: string,
  body: Record<string, unknown> | null,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);
  const refusal = useRefusalText();
  const serialized = body ? JSON.stringify(body) : null;

  useEffect(() => {
    if (!serialized) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = (await API.post({
          path,
          body: JSON.parse(serialized) as Record<string, unknown>,
        })) as ApiAnswer | null;
        if (ticket !== latest.current) return;
        if (res?.ok && res.data) {
          setData(res.data as T);
          setError(null);
        } else {
          setData(null);
          setError(refusal(String(res?.error || res?.detail || "")));
        }
      } catch {
        if (ticket === latest.current) {
          setData(null);
          setError(refusal(""));
        }
      } finally {
        if (ticket === latest.current) setLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [path, serialized, refusal]);

  return { data, loading, error };
}
